import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Calendar, Save, RefreshCw, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Link2, Plus, Trash2, Activity, Radio, RotateCw, Power } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface GoogleConfig {
  id: number;
  service_account_email: string | null;
  service_account_private_key: string | null;
  admin_delegated_email: string | null;
  sync_enabled: boolean;
  webhook_secret: string | null;
  last_full_sync_at: string | null;
}

interface SalaConfig {
  id: string;
  nome: string;
  capacidade: number;
  cor: string;
  ativo: boolean;
  google_resource_email: string | null;
  google_watch_channel_id: string | null;
  google_watch_expiration: string | null;
}

interface LogEntry {
  id: string;
  direcao: string;
  acao: string;
  status: string;
  erro: string | null;
  google_event_id: string | null;
  created_at: string;
}

export const GoogleCalendarAdmin: React.FC = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<GoogleConfig | null>(null);
  const [salas, setSalas] = useState<SalaConfig[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [privateKey, setPrivateKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.setor === 'TI';

  useEffect(() => {
    if (!isAdmin) return;
    void loadAll();
  }, [isAdmin]);

  async function loadAll() {
    setLoading(true);
    try {
      const [{ data: cfg }, { data: sList }, { data: lList }] = await Promise.all([
        supabase.from('google_calendar_config').select('*').eq('id', 1).maybeSingle(),
        supabase.from('salas_config').select('*').order('nome'),
        supabase.from('google_sync_log').select('*').order('created_at', { ascending: false }).limit(20),
      ]);
      setConfig(
        cfg || {
          id: 1,
          service_account_email: '',
          service_account_private_key: '',
          admin_delegated_email: '',
          sync_enabled: false,
          webhook_secret: '',
          last_full_sync_at: null,
        }
      );
      setSalas(sList || []);
      setLogs(lList || []);
    } catch (err) {
      console.error(err);
      toast.error('Nao foi possivel carregar a configuracao');
    } finally {
      setLoading(false);
    }
  }

  async function saveConfig() {
    if (!config) return;
    setSaving(true);
    try {
      const patch: any = {
        service_account_email: config.service_account_email,
        admin_delegated_email: config.admin_delegated_email,
        sync_enabled: config.sync_enabled,
        webhook_secret: config.webhook_secret,
        updated_at: new Date().toISOString(),
      };
      if (privateKey.trim()) patch.service_account_private_key = privateKey.trim();
      const { error } = await supabase.from('google_calendar_config').update(patch).eq('id', 1);
      if (error) throw error;
      toast.success('Configuracao salva');
      setPrivateKey('');
      await loadAll();
    } catch (err: any) {
      toast.error('Erro ao salvar: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  }

  async function updateSalaEmail(id: string, google_resource_email: string) {
    try {
      const { error } = await supabase
        .from('salas_config')
        .update({ google_resource_email, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      toast.success('Sala atualizada');
      await loadAll();
    } catch (err: any) {
      toast.error('Erro: ' + (err.message || err));
    }
  }

  async function invokeSync(body: Record<string, unknown>, loadingMsg: string, successMsg: string) {
    const t = toast.loading(loadingMsg);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', { body });
      if (error) throw error;
      if (data && data.error) throw new Error(data.error);
      toast.success(successMsg, { id: t });
      await loadAll();
      return data;
    } catch (err: any) {
      toast.error('Erro: ' + (err.message || err), { id: t });
      return null;
    }
  }

  async function ativarSalaWatch(sala: SalaConfig) {
    if (!sala.google_resource_email) {
      toast.error('Preencha o e-mail do recurso desta sala antes de ativar.');
      return;
    }
    await invokeSync({ action: 'watch', sala_id: sala.id }, `Ativando ${sala.nome}...`, `${sala.nome} sincronizada`);
  }

  async function desativarSalaWatch(sala: SalaConfig) {
    await invokeSync({ action: 'stop-watch', sala_id: sala.id }, `Desativando ${sala.nome}...`, `${sala.nome} desativada`);
  }

  async function renovarTodos() {
    await invokeSync({ action: 'renew-watches' }, 'Renovando canais...', 'Canais renovados');
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="p-8 text-center text-secondary">Acesso restrito.</div>
      </Layout>
    );
  }

  if (loading || !config) {
    return (
      <Layout>
        <div className="p-8 text-center text-secondary">Carregando configuracao...</div>
      </Layout>
    );
  }

  const configured = !!config.service_account_email && !!config.admin_delegated_email;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-headline-lg text-on-surface mb-1">Sincronizacao Google Calendar</h1>
            <p className="text-body-md text-secondary">
              Configure a integracao bidirecional entre a Intranet e o Google Workspace.
            </p>
          </div>
          <button
            onClick={loadAll}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-outline-variant text-on-surface hover:bg-surface-container-low transition"
          >
            <RefreshCw className="w-4 h-4" /> Atualizar
          </button>
        </div>

        <div className={`rounded-xl p-5 border-2 flex items-center justify-between ${
          config.sync_enabled && configured
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500/40'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-500/40'
        }`}>
          <div className="flex items-center gap-3">
            {config.sync_enabled && configured ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-amber-600" />
            )}
            <div>
              <div className="font-bold text-on-surface">
                {config.sync_enabled && configured
                  ? 'Sincronizacao ativa'
                  : configured
                  ? 'Sincronizacao pausada'
                  : 'Sincronizacao nao configurada'}
              </div>
              <div className="text-sm text-secondary">
                {config.last_full_sync_at
                  ? 'Ultima sincronizacao: ' + new Date(config.last_full_sync_at).toLocaleString('pt-BR')
                  : 'Nunca sincronizado'}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.sync_enabled}
              onChange={(e) => setConfig({ ...config, sync_enabled: e.target.checked })}
              className="w-5 h-5 accent-primary"
            />
            <span className="font-medium text-on-surface">Ativa</span>
          </label>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
          <h2 className="text-lg font-bold text-on-surface mb-1">Credenciais da conta de servico</h2>
          <p className="text-sm text-secondary mb-5">
            Cole os dados da conta de servico criada no Google Cloud com delegacao em todo o dominio autorizada.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-on-surface mb-2">E-mail da conta de servico</label>
              <input
                type="email"
                value={config.service_account_email || ''}
                onChange={(e) => setConfig({ ...config, service_account_email: e.target.value })}
                placeholder="intranet-sync@projeto.iam.gserviceaccount.com"
                className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface mb-2">
                E-mail admin (delegacao)
              </label>
              <input
                type="email"
                value={config.admin_delegated_email || ''}
                onChange={(e) => setConfig({ ...config, admin_delegated_email: e.target.value })}
                placeholder="admin@seudominio.com.br"
                className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-on-surface mb-2 flex items-center justify-between">
                <span>Chave privada da conta de servico (PEM)</span>
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  className="text-xs text-primary underline"
                >
                  {showKey ? 'Ocultar' : 'Mostrar'}
                </button>
              </label>
              <textarea
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                rows={showKey ? 8 : 3}
                placeholder={
                  config.service_account_private_key
                    ? '(chave ja salva - deixe em branco para manter)'
                    : '-----BEGIN PRIVATE KEY-----\nMIIEv...\n-----END PRIVATE KEY-----'
                }
                className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface font-mono text-xs"
                style={{ filter: showKey ? 'none' : 'blur(0)' }}
              />
              <p className="text-xs text-secondary mt-1">
                A chave e guardada de forma protegida no banco e nunca e enviada de volta ao navegador.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-on-surface mb-2">Token de webhook</label>
              <input
                type="text"
                value={config.webhook_secret || ''}
                onChange={(e) => setConfig({ ...config, webhook_secret: e.target.value })}
                placeholder="ex: um token aleatorio, minimo 20 caracteres"
                className="w-full px-3 py-2.5 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface"
              />
              <p className="text-xs text-secondary mt-1">
                Valida chamadas do Google no webhook.
              </p>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <button
              onClick={saveConfig}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white font-bold hover:bg-primary-dark disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar configuracao'}
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-1">
            <h2 className="text-lg font-bold text-on-surface">Salas e recursos do Google Workspace</h2>
            <button
              onClick={renovarTodos}
              disabled={!config.sync_enabled || !configured}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-dark disabled:opacity-50"
              title={!config.sync_enabled ? 'Ative a sincronizacao primeiro' : 'Renovar todos os canais expirando'}
            >
              <RotateCw className="w-4 h-4" /> Renovar todos
            </button>
          </div>
          <p className="text-sm text-secondary mb-5">
            Vincule cada sala da Intranet ao e-mail do recurso correspondente no Google Workspace e ative a sincronizacao bidirecional em cada uma.
          </p>
          <div className="space-y-3">
            {salas.map((s) => {
              const expiration = s.google_watch_expiration ? new Date(s.google_watch_expiration) : null;
              const isActive = !!s.google_watch_channel_id && expiration && expiration.getTime() > Date.now();
              const isExpiringSoon = expiration && expiration.getTime() - Date.now() < 24 * 60 * 60 * 1000;
              return (
                <div
                  key={s.id}
                  className="flex flex-col gap-3 p-3 rounded-lg border border-outline-variant"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex items-center gap-3 md:w-56">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: s.cor }} />
                      <div>
                        <div className="font-bold text-on-surface">{s.nome}</div>
                        <div className="text-xs text-secondary">
                          {s.capacidade} pessoas
                          {!s.ativo && <span className="ml-2 text-error">Inativa</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-secondary" />
                        <input
                          type="email"
                          defaultValue={s.google_resource_email || ''}
                          onBlur={(e) => {
                            if ((e.target.value || null) !== (s.google_resource_email || null)) {
                              void updateSalaEmail(s.id, e.target.value);
                            }
                          }}
                          placeholder="sala-xxx@seudominio.com.br"
                          className="flex-1 px-3 py-2 border border-outline-variant rounded-lg bg-surface-container-lowest text-on-surface text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 pl-0 md:pl-59">
                    <div className="flex items-center gap-2 text-xs">
                      <Radio className={`w-4 h-4 ${isActive ? (isExpiringSoon ? 'text-amber-500' : 'text-emerald-500') : 'text-secondary'}`} />
                      {isActive ? (
                        <span className={isExpiringSoon ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}>
                          Bidirecional ativa
                          {expiration && ` - expira ${expiration.toLocaleString('pt-BR')}`}
                        </span>
                      ) : (
                        <span className="text-secondary">Somente saida (Intranet {'->'} Google)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isActive ? (
                        <>
                          <button
                            onClick={() => ativarSalaWatch(s)}
                            disabled={!config.sync_enabled || !configured}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary text-primary text-xs font-bold hover:bg-primary/10 disabled:opacity-50"
                          >
                            <RotateCw className="w-3.5 h-3.5" /> Renovar
                          </button>
                          <button
                            onClick={() => desativarSalaWatch(s)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-xs font-bold hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Power className="w-3.5 h-3.5" /> Desativar
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => ativarSalaWatch(s)}
                          disabled={!config.sync_enabled || !configured || !s.google_resource_email}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary-dark disabled:opacity-50"
                          title={
                            !s.google_resource_email
                              ? 'Preencha o e-mail do recurso'
                              : !config.sync_enabled
                              ? 'Ative a sincronizacao primeiro'
                              : 'Ativar sincronizacao bidirecional'
                          }
                        >
                          <Radio className="w-3.5 h-3.5" /> Ativar bidirecional
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-secondary mt-4">
            Ao ativar a bidirecional, o Google avisa a Intranet toda vez que alguem cria, altera ou cancela um evento na sala pelo Google Calendar. Os canais expiram a cada 7 dias e devem ser renovados. Clique em "Renovar todos" para atualizar todas as salas de uma vez.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-on-surface">Historico recente de sincronizacao</h2>
          </div>
          {logs.length === 0 ? (
            <p className="text-sm text-secondary">Nenhuma sincronizacao registrada ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-secondary border-b border-outline-variant">
                    <th className="py-2 pr-3">Quando</th>
                    <th className="py-2 pr-3">Direcao</th>
                    <th className="py-2 pr-3">Acao</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2">Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b border-outline-variant/50">
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString('pt-BR')}
                      </td>
                      <td className="py-2 pr-3">
                        {l.direcao === 'inbound' ? 'Google -> Intranet' : 'Intranet -> Google'}
                      </td>
                      <td className="py-2 pr-3 capitalize">{l.acao}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            l.status === 'success'
                              ? 'bg-emerald-100 text-emerald-800'
                              : l.status === 'conflict'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {l.status}
                        </span>
                      </td>
                      <td className="py-2 text-secondary truncate max-w-xs">
                        {l.erro || l.google_event_id || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <details className="bg-surface-container-low rounded-xl p-5 border border-outline-variant">
          <summary className="cursor-pointer font-bold text-on-surface">Passo a passo no Google Cloud</summary>
          <ol className="mt-3 space-y-2 text-sm text-secondary list-decimal list-inside">
            <li>Crie um projeto no Google Cloud e ative a Google Calendar API.</li>
            <li>Crie uma conta de servico e gere uma chave privada em formato JSON.</li>
            <li>
              No Google Workspace Admin, autorize a delegacao em todo o dominio usando o Client ID da conta de servico com o escopo{' '}
              <code>https://www.googleapis.com/auth/calendar</code>.
            </li>
            <li>Cadastre as salas como recursos no Google Workspace (cada uma ganha um e-mail proprio).</li>
            <li>Cole o e-mail da conta de servico, o e-mail de um admin do Workspace e a chave privada acima.</li>
            <li>Preencha o e-mail de cada sala na tabela acima.</li>
            <li>Ative a sincronizacao e publique as funcoes de servidor <code>google-calendar-sync</code> e <code>google-calendar-webhook</code>.</li>
          </ol>
        </details>
      </div>
    </Layout>
  );
};

export default GoogleCalendarAdmin;
